import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import Sources from '../../components/Sources';
import FAQSection from '../../components/FAQSection';
import TableOfContents from '../../components/TableOfContents';
import Callout from '../../components/Callout';
import Breadcrumbs from '../../components/Breadcrumbs';

export default function TrailerHastighed() {
  const breadcrumbs = [
    { name: "Forside", path: "/" },
    { name: "Hastighed og kørsel", path: "/trailer-hastighed" }
  ];
  const tocItems = [
    { id: "hastighedsgraenser-overblik", title: "Overblik over hastighedsgrænser i Danmark" },
    { id: "motorvej-tempo-100", title: "Kørsel på motorvej og Tempo 100" },
    { id: "motortrafikvej-landevej", title: "Landevej og motortrafikvej (80 km/t)" },
    { id: "byzone", title: "Byzone og lokale hastighedstavler" },
    { id: "boeder-klip", title: "Bødetakster, klip og frakendelse ved trailerkørsel" }
  ];

  const faq = [
    {
      q: "Hvor hurtigt må man køre med trailer på motorvej i Danmark?",
      a: "Standardhastigheden for bil med trailer på dansk motorvej er 80 km/t. Hvis traileren og vogntoget er godkendt og mærket til dansk Tempo 100, må du køre op til 100 km/t, såfremt skiltningen tillader det."
    },
    {
      q: "Må man køre 90 km/t på motortrafikvej med trailer, hvis vejen er skiltet til 90?",
      a: "Nej. Den generelle hastighedsgrænse for vogntog med trailer uden for byzone er 80 km/t på motortrafikvej og landevej. Du må aldrig køre hurtigere end 80 km/t på motortrafikvej, selvom personbiler uden trailer må køre 90 eller 100 km/t."
    },
    {
      q: "Gælder et tysk Tempo 100-mærke i Danmark?",
      a: "Nej. Et tysk Tempo 100-mærke gælder kun i Tyskland. For at køre 100 km/t på danske motorveje skal traileren have et officielt dansk Tempo 100-mærke udstedt af Færdselsstyrelsen."
    },
    {
      q: "Hvornår får man klip i kørekortet for hastighed med trailer?",
      a: "For køretøjer med påhængsvogn udløses et klip i kørekortet allerede ved en hastighedsoverskridelse på mere end 30 % i forhold til den gældende grænse (f.eks. ved kørsel over 105 km/t på motorvej med standardtrailer)."
    }
  ];

  const sources = [
    { title: "Færdselsloven (LBK nr. 1324 af 18/09/2023) § 42 og § 43 om hastighed", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2023/1324" },
    { title: "Bekendtgørelse om Tempo 100 for påhængskøretøjer (BEK nr. 1614 af 13/12/2016)", source: "retsinformation.dk", url: "https://www.retsinformation.dk/eli/lta/2016/1614" },
    { title: "Færdselsstyrelsen: Krav og regler for Tempo 100 påhængskøretøjer", source: "fstyr.dk", url: "https://fstyr.dk/privat/krav-til-koeretoejer/tempo-100" }
  ];

  return (
    <>
      <SEO 
        title="Trailer hastighed: Grænser på motorvej og landevej" 
        description="Få overblik over hastighedsgrænser med trailer i Danmark: by (50), landevej (80), motortrafikvej (80) og motorvej (80 eller 100 km/t med Tempo 100)." 
        pathname="/trailer-hastighed"
        faq={faq}
        breadcrumbs={breadcrumbs}
      />

      <article>
        <Breadcrumbs items={breadcrumbs} />
        <h1 className="text-[30px] sm:text-[36px] font-bold text-[#2E3A46] tracking-tight leading-tight mb-4">
          Hastighedsgrænser med trailer i Danmark
        </h1>

        <p className="text-[18px] text-[#1A1C1F] leading-relaxed mb-6 font-medium">
          I Danmark er den generelle hastighedsgrænse for en personbil med trailer <strong>80 km/t på motorvej</strong>, <strong>80 km/t på landevej og motortrafikvej</strong> samt <strong>50 km/t i byzoner</strong>.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Den eneste undtagelse på motorvej er, hvis traileren er godkendt til dansk <em>Tempo 100</em>. Med denne godkendelse må du køre op til 100 km/t på motorvejsstrækninger, hvor skiltningen tillader det. På landevej og motortrafikvej er grænsen altid højst 80 km/t, uanset om traileren har Tempo 100 eller ej.
        </p>

        <TableOfContents items={tocItems} />

        <Callout title="Hurtigt overblik over grænser">
          <ul className="m-0 p-0 list-none space-y-1 font-mono text-[15px]">
            <li>• Byzone: <strong>50 km/t</strong> (medmindre lavere/højere er skiltet)</li>
            <li>• Landevej / Motortrafikvej: <strong>80 km/t</strong></li>
            <li>• Motorvej (standard): <strong>80 km/t</strong></li>
            <li>• Motorvej (med Tempo 100): <strong>100 km/t</strong></li>
          </ul>
        </Callout>

        <h2 id="hastighedsgraenser-overblik" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Overblik over hastighedsgrænser i Danmark
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Nedenstående tabel viser de gældende generelle grænser for personbil med trailer i Danmark:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Vejtype</th>
                <th className="py-2.5 pr-4 font-semibold">Almindelig trailer</th>
                <th className="py-2.5 pr-4 font-semibold">Tempo 100-godkendt</th>
                <th className="py-2.5 font-semibold">Regelgrundlag</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Byzone</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 pr-4">50 km/t</td>
                <td className="py-3 text-[#5C636B]">Færdselslovens § 42, stk. 1</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Landevej</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">Færdselslovens § 43, stk. 2</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Motortrafikvej</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 pr-4">80 km/t</td>
                <td className="py-3 text-[#5C636B]">Færdselslovens § 43, stk. 2</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Motorvej</td>
                <td className="py-3 pr-4 font-semibold text-[#2E3A46]">80 km/t</td>
                <td className="py-3 pr-4 font-bold text-[#B58200]">100 km/t</td>
                <td className="py-3 text-[#5C636B]">Færdselslovens § 43, stk. 3</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2 id="motorvej-tempo-100" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Kørsel på motorvej og Tempo 100
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          På motorveje er den normale fartgrænse 80 km/t for alle vogntog. Det gælder uanset trailerens størrelse – fra den mindste haveaffaldstrailer til en tung bådtrailer eller hestetrailer.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Hvis du ønsker at køre 100 km/t, skal traileren være synet og godkendt til dansk <strong>Tempo 100</strong> hos en synshal. Dette stiller specifikke tekniske krav:
        </p>

        <ul className="list-disc list-inside space-y-2 mb-6 text-[#1A1C1F]">
          <li>Dækkene må højst være 6 år gamle og skal være godkendt til mindst 120 km/t (hastighedsindeks L).</li>
          <li>Traileren skal være forsynet med hydrauliske støddæmpere (hvis bremset).</li>
          <li>Bilen skal have ABS-bremser og opfylde specifikke vægtforhold i forhold til traileren.</li>
        </ul>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Læs samtlige tekniske krav og beregninger i vores dybdegående artikel om <Link to="/tempo-100" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">Tempo 100 godkendelse</Link>.
        </p>

        <h2 id="motortrafikvej-landevej" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Landevej og motortrafikvej (80 km/t)
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Tidligere måtte man kun køre 70 km/t med trailer på landeveje i Danmark. Denne grænse blev harmoniseret, så den generelle grænse uden for byzone i dag er <strong>80 km/t</strong> for både landevej og motortrafikvej.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Bemærk: Hvis en motortrafikvej er skiltet med lokal hastighedsbegrænsning på 90 eller 100 km/t for almindelige personbiler, må et vogntog med trailer stadig <strong>højst køre 80 km/t</strong>. Den lokale skiltning hæver aldrig grænsen for vogntog over 80 km/t uden for motorvej.
        </p>

        <h2 id="byzone" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Byzone og lokale hastighedstavler
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          I byzone gælder den generelle grænse på 50 km/t. Hvis der lokalt er skiltet med en lavere grænse (f.eks. 30 eller 40 km/t), skal denne selvfølgelig respekteres.
        </p>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Hvis en indfaldsvej i en byzone er skiltet med 60 eller 70 km/t, må en bil med trailer normalt følge den skiltede hastighed, så længe den ikke overstiger 70 km/t, medmindre der specifikt er opsat undertavler med forbud for påhængskøretøjer.
        </p>

        <h2 id="boeder-klip" className="text-[22px] font-bold text-[#2E3A46] mt-10 mb-4">
          Bødetakster, klip og frakendelse ved trailerkørsel
        </h2>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-4">
          Det koster væsentligt hårdere sanktioner at køre for stærkt med trailer end i en almindelig personbil:
        </p>

        <div className="my-6 overflow-x-auto border-t border-[#B58200]">
          <table className="w-full text-left text-[15px] border-collapse">
            <thead>
              <tr className="border-b border-[#E4E1D8] font-mono text-[13px] uppercase text-[#2E3A46]">
                <th className="py-2.5 pr-4 font-semibold">Overskridelse</th>
                <th className="py-2.5 pr-4 font-semibold">Faktisk fart (på 80-vej)</th>
                <th className="py-2.5 font-semibold">Sanktion for vogntog</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E4E1D8] tabular-nums">
              <tr>
                <td className="py-3 pr-4">1 – 30 %</td>
                <td className="py-3 pr-4">81 – 104 km/t</td>
                <td className="py-3 text-[#5C636B]">Bøde efter faste takster + højhastighedstillæg</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-semibold text-[#B58200]">Over 30 %</td>
                <td className="py-3 pr-4 font-semibold text-[#B58200]">105 – 112 km/t</td>
                <td className="py-3 font-semibold text-[#B58200]">1 klip i kørekortet + bøde</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Over 40 %</td>
                <td className="py-3 pr-4 font-bold text-[#2E3A46]">Over 112 km/t</td>
                <td className="py-3 font-bold text-[#2E3A46]">Betinget frakendelse af kørekortet</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="text-[17px] text-[#1A1C1F] leading-relaxed mb-6">
          Skal du køre sydpå eller nordpå med trailer? Tjek reglerne i vores nabolande i artiklerne om <Link to="/trailer-i-tyskland" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i Tyskland</Link>, <Link to="/trailer-i-sverige" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i Sverige</Link> og kørsel i <Link to="/trailer-i-udlandet" className="text-[#2E3A46] font-medium hover:text-[#B58200] hover:underline underline-offset-2">trailer i udlandet</Link>.
        </p>

        <FAQSection items={faq} />
        <Sources links={sources} />
      </article>
    </>
  );
}
